import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import ImportUsersButton from "../../components/ImportUsersButton";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { PencilSimple, Trash, ToggleLeft, ToggleRight, MagnifyingGlass, X, ShieldCheck, CheckSquare, Square, Coin, Key, Plus, Eye, EyeSlash, ClipboardText } from "@phosphor-icons/react";

const ROLES = ["student", "examiner", "institution_admin", "proctor", "super_admin"];

export default function UsersList() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // user object
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null); // action string
  const [bulkBusy, setBulkBusy] = useState(false);
  const [creditingUser, setCreditingUser] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [allocUser, setAllocUser] = useState(null);
  const [bulkAllocOpen, setBulkAllocOpen] = useState(false);

  const canAssignSuper = me?.role === "super_admin";
  const canManageUsers = me?.role === "institution_admin" || me?.role === "super_admin";

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (statusFilter === "active" && u.is_active === false) return false;
      if (statusFilter === "disabled" && u.is_active !== false) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, search, roleFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.is_active !== false).length,
    disabled: users.filter((u) => u.is_active === false).length,
  }), [users]);

  const toggleActive = async (u) => {
    if (u.user_id === me?.user_id) { toast.error("You cannot disable your own account"); return; }
    setBusyId(u.user_id);
    try {
      const r = await api.post(`/users/${u.user_id}/toggle-active`);
      toast.success(r.data.is_active ? "User enabled" : "User disabled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const doDelete = async (u) => {
    setBusyId(u.user_id);
    try {
      const r = await api.delete(`/users/${u.user_id}`);
      if (r.data?.soft_deleted) toast.success(r.data.message || "User deactivated");
      else toast.success("User deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  // Bulk selection helpers
  const toggleSel = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const selectableIds = filtered.filter((u) => u.user_id !== me?.user_id).map((u) => u.user_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/users/bulk-action", { user_ids: Array.from(selected), action });
      const skipped = r.data.skipped?.length || 0;
      const msg =
        action === "delete"
          ? `${r.data.hard_deleted} deleted, ${r.data.soft_deleted} deactivated${skipped ? `, ${skipped} skipped` : ""}`
          : `${r.data.processed} ${action}d${skipped ? `, ${skipped} skipped` : ""}`;
      toast.success(msg);
      clearSelection();
      setBulkConfirm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <div>
      <PageHeader
        overline="People"
        title="Users"
        subtitle="Edit, enable, disable, or remove members of your institution. Disabled users cannot sign in."
        action={<ImportUsersButton onImported={() => window.location.reload()} />}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 border border-zinc-200 mb-6" data-testid="users-stats">
        <Stat label="Total users" value={stats.total} />
        <Stat label="Active" value={stats.active} tone="emerald" />
        <Stat label="Disabled" value={stats.disabled} tone="red" />
      </div>

      {/* Pending role requests */}
      <PendingRoleRequests />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            data-testid="users-search-input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 text-sm bg-white"
          data-testid="users-role-filter"
        >
          <option value="all">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 text-sm bg-white"
          data-testid="users-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="disabled">Disabled only</option>
        </select>
        {canManageUsers && (
          <button
            onClick={() => setBulkAllocOpen(true)}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-900"
            data-testid="users-bulk-alloc-btn"
          >
            <ClipboardText size={14} /> Bulk assign exams
          </button>
        )}
        {canManageUsers && (
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm hover:bg-zinc-800"
            data-testid="users-add-btn"
          >
            <Plus size={14} weight="bold" /> Add user
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 border border-zinc-900 bg-zinc-900 text-white px-4 py-3" data-testid="users-bulk-bar">
          <div className="flex-1 text-sm font-medium">{selected.size} selected</div>
          <button onClick={() => setBulkConfirm("enable")} disabled={bulkBusy} className="text-xs uppercase tracking-wider border border-white/40 px-3 py-1.5 hover:bg-white/10 disabled:opacity-40" data-testid="users-bulk-enable">Enable</button>
          <button onClick={() => setBulkConfirm("disable")} disabled={bulkBusy} className="text-xs uppercase tracking-wider border border-white/40 px-3 py-1.5 hover:bg-white/10 disabled:opacity-40" data-testid="users-bulk-disable">Disable</button>
          <button onClick={() => setBulkConfirm("delete")} disabled={bulkBusy} className="text-xs uppercase tracking-wider bg-red-600 border border-red-600 px-3 py-1.5 hover:bg-red-700 disabled:opacity-40" data-testid="users-bulk-delete">Delete</button>
          <button onClick={clearSelection} className="text-xs uppercase tracking-wider text-white/70 hover:text-white ml-2" data-testid="users-bulk-clear">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="users-empty">No users match your filters.</div>
      ) : (
        <div className="border border-zinc-200 overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <tr>
                <th className="px-3 py-3 w-8">
                  <button onClick={toggleSelectAll} className="text-zinc-700 hover:text-zinc-900" data-testid="users-select-all" aria-label="Select all">
                    {allSelected ? <CheckSquare size={16} weight="fill" /> : <Square size={16} />}
                  </button>
                </th>
                <th className="px-5 py-3 text-left font-semibold">Name</th>
                <th className="px-5 py-3 text-left font-semibold">Email</th>
                <th className="px-5 py-3 text-left font-semibold">Role</th>
                <th className="px-5 py-3 text-left font-semibold">Status</th>
                <th className="px-5 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => {
                const isSelf = u.user_id === me?.user_id;
                const isDisabled = u.is_active === false;
                const isSel = selected.has(u.user_id);
                return (
                  <tr key={u.user_id} className={`${i < filtered.length - 1 ? "border-b border-zinc-200" : ""} ${isDisabled ? "bg-zinc-50/50 opacity-70" : ""} ${isSel ? "bg-blue-50/30" : ""}`} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-3 py-3">
                      {!isSelf && (
                        <button onClick={() => toggleSel(u.user_id)} className="text-zinc-600 hover:text-zinc-900" data-testid={`user-select-${u.user_id}`} aria-label={isSel ? "Deselect" : "Select"}>
                          {isSel ? <CheckSquare size={16} weight="fill" /> : <Square size={16} />}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium">{u.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider border border-zinc-300 px-1.5 py-0.5">You</span>}</div>
                      <div className="text-[10px] text-zinc-400 font-mono">{u.user_id}</div>
                    </td>
                    <td className="px-5 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className="inline-block text-[10px] uppercase tracking-wider border border-zinc-300 px-2 py-1 bg-white">
                        {u.role?.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {isDisabled ? (
                        <span className="inline-block text-[10px] uppercase tracking-wider border border-red-300 text-red-700 bg-red-50 px-2 py-1">Disabled</span>
                      ) : (
                        <span className="inline-block text-[10px] uppercase tracking-wider border border-emerald-300 text-emerald-700 bg-emerald-50 px-2 py-1 inline-flex items-center gap-1">
                          <ShieldCheck size={10} weight="fill" /> Active
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {u.role === "student" && (
                          <button
                            onClick={() => setCreditingUser(u)}
                            className="p-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1"
                            data-testid={`user-credit-${u.user_id}`}
                            title={`Credit wallet · balance ${u.credits ?? 0}`}
                          >
                            <Coin size={14} weight="fill" />
                            <span className="text-[10px] font-mono">{u.credits ?? 0}</span>
                          </button>
                        )}
                        <button
                          onClick={() => toggleActive(u)}
                          disabled={isSelf || busyId === u.user_id}
                          className={`p-1.5 border ${isDisabled ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"} disabled:opacity-40`}
                          data-testid={`user-toggle-active-${u.user_id}`}
                          title={isDisabled ? "Enable user" : "Disable user"}
                        >
                          {isDisabled ? <ToggleLeft size={14} /> : <ToggleRight size={14} weight="fill" />}
                        </button>
                        <button
                          onClick={() => setEditing(u)}
                          className="p-1.5 border border-zinc-300 hover:bg-zinc-50"
                          data-testid={`user-edit-${u.user_id}`}
                          title="Edit user"
                        >
                          <PencilSimple size={14} />
                        </button>
                        {canManageUsers && (
                          <button
                            onClick={() => setResetPwUser(u)}
                            className="p-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid={`user-reset-password-${u.user_id}`}
                            title="Reset password"
                          >
                            <Key size={14} />
                          </button>
                        )}
                        {canManageUsers && u.role === "student" && (
                          <button
                            onClick={() => setAllocUser(u)}
                            className="p-1.5 border border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid={`user-assign-exams-${u.user_id}`}
                            title="Assign exams"
                          >
                            <ClipboardText size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDelete(u)}
                          disabled={isSelf}
                          className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
                          data-testid={`user-delete-${u.user_id}`}
                          title="Delete user"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserEditModal
          user={editing}
          canAssignSuper={canAssignSuper}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="user-delete-confirm">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-2">Delete {confirmDelete.name}?</div>
            <div className="text-sm text-zinc-600 mb-5">
              If this user owns exams or has attempts, they will be deactivated instead of hard-deleted (audit-safe). Otherwise the account is fully removed.
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900"
                data-testid="user-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => doDelete(confirmDelete)}
                disabled={busyId === confirmDelete.user_id}
                className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                data-testid="user-delete-confirm-btn"
              >
                {busyId === confirmDelete.user_id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="users-bulk-confirm">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-2 capitalize">{bulkConfirm} {selected.size} users?</div>
            <div className="text-sm text-zinc-600 mb-5">
              {bulkConfirm === "delete"
                ? "Users with exams or attempts will be soft-deleted (deactivated) to preserve records. Others will be permanently removed."
                : bulkConfirm === "disable"
                  ? "Disabled users will not be able to sign in until re-enabled."
                  : "Enabled users can sign in immediately."}
              <br /><span className="text-xs text-zinc-500">Super admins and out-of-scope users are automatically skipped.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkConfirm(null)} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900" data-testid="users-bulk-cancel">
                Cancel
              </button>
              <button
                onClick={() => runBulk(bulkConfirm)}
                disabled={bulkBusy}
                className={`px-4 py-2 text-white text-sm disabled:opacity-50 ${bulkConfirm === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-900 hover:bg-zinc-800"}`}
                data-testid="users-bulk-confirm-btn"
              >
                {bulkBusy ? "Working…" : `${bulkConfirm} ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {creditingUser && (
        <CreditModal
          user={creditingUser}
          onClose={() => setCreditingUser(null)}
          onSaved={() => { setCreditingUser(null); load(); }}
        />
      )}
      {addOpen && (
        <AddUserModal
          me={me}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {resetPwUser && (
        <ResetPasswordModal
          user={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onSaved={() => setResetPwUser(null)}
        />
      )}
      {allocUser && (
        <AssignExamsModal
          user={allocUser}
          onClose={() => setAllocUser(null)}
          onSaved={() => setAllocUser(null)}
        />
      )}
      {bulkAllocOpen && (
        <BulkAssignExamsModal
          onClose={() => setBulkAllocOpen(false)}
        />
      )}
    </div>
  );
}

function CreditModal({ user, onClose, onSaved }) {
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [instBalance, setInstBalance] = useState(null);
  useEffect(() => {
    if (user?.institution_id) {
      api.get(`/institutions/${user.institution_id}/wallet`)
        .then((r) => setInstBalance(r.data.balance))
        .catch(() => setInstBalance(null));
    }
  }, [user]);
  const submit = async () => {
    if (!amount || amount <= 0) { toast.error("Amount must be positive"); return; }
    setSaving(true);
    try {
      const r = await api.post("/wallet/credit", { user_id: user.user_id, amount: Number(amount), reason });
      toast.success(`+${amount} credits added. New balance: ${r.data.balance}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const insufficient = instBalance != null && amount > instBalance;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="credit-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Credit wallet</div>
            <div className="font-display text-lg font-bold">{user.name}</div>
            <div className="text-xs text-zinc-500">Student balance: <span className="font-mono font-bold">{user.credits ?? 0}</span> coins</div>
            {instBalance != null && (
              <div className="text-xs text-amber-800 mt-1">Institution wallet: <span className="font-mono font-bold" data-testid="credit-inst-balance">{instBalance.toLocaleString()}</span> coins available</div>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="credit-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Amount to credit</div>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="credit-amount-input"
            />
            <div className="flex gap-2 mt-2">
              {[100, 500, 1000, 2500].map((v) => (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  className="text-xs border border-zinc-300 px-2 py-1 hover:bg-zinc-50"
                  data-testid={`credit-preset-${v}`}
                >
                  +{v}
                </button>
              ))}
            </div>
            {insufficient && (
              <div className="text-xs text-red-700 mt-2 border border-red-300 bg-red-50 p-2" data-testid="credit-insufficient">
                Institution wallet has only {instBalance.toLocaleString()} coins. Ask super admin to fund the institution.
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Reason (optional)</div>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. New batch onboarding"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="credit-reason-input"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || insufficient}
            className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="credit-submit"
          >
            <Coin size={14} weight="fill" /> {saving ? "Crediting…" : `Credit ${amount || 0}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "zinc" }) {
  const tones = { zinc: "text-zinc-900", emerald: "text-emerald-700", red: "text-red-700" };
  return (
    <div className="p-5 border-r border-zinc-200 last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold tabular ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function UserEditModal({ user, canAssignSuper, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "student",
    is_active: user.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const availableRoles = canAssignSuper ? ROLES : ROLES.filter((r) => r !== "super_admin");

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      await api.put(`/users/${user.user_id}`, form);
      toast.success("User updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="user-edit-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Edit user</div>
            <div className="font-display text-lg font-bold">{user.name}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="user-edit-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Name</div>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="user-edit-name"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Email</div>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="user-edit-email"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Role</div>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="user-edit-role"
            >
              {availableRoles.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="user-edit-active"
            />
            <span className="text-sm">Account active (can sign in)</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
            data-testid="user-edit-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


function AddUserModal({ me, onClose, onSaved }) {
  const isSuper = me?.role === "super_admin";
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "student",
    institution_id: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [institutions, setInstitutions] = useState([]);

  useEffect(() => {
    if (isSuper) {
      api.get("/institutions").then((r) => setInstitutions(r.data || [])).catch(() => {});
    }
  }, [isSuper]);

  const availableRoles = isSuper
    ? ["student", "examiner", "proctor", "institution_admin"]
    : ["student", "examiner", "proctor", "institution_admin"];

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    if (!form.password || form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
      };
      if (isSuper && form.institution_id) payload.institution_id = form.institution_id;
      await api.post("/users", payload);
      toast.success("User created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="user-add-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Create user</div>
            <div className="font-display text-lg font-bold">Add a new member</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="user-add-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Name</div>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="user-add-name"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Email</div>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="user-add-email"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Password (min 8 chars)</div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 pr-10 text-sm focus:border-zinc-900 focus:outline-none font-mono"
                data-testid="user-add-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
                data-testid="user-add-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Role</div>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="user-add-role"
            >
              {availableRoles.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>
          {isSuper && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Institution (optional)</div>
              <select
                value={form.institution_id}
                onChange={(e) => setForm({ ...form, institution_id: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                data-testid="user-add-institution"
              >
                <option value="">— None —</option>
                {institutions.map((i) => (
                  <option key={i.institution_id} value={i.institution_id}>{i.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
            data-testid="user-add-submit"
          >
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.user_id}/password`, { password });
      toast.success(`Password updated for ${user.name}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update password"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="user-reset-password-modal">
      <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Reset password</div>
            <div className="font-display text-lg font-bold">{user.name}</div>
            <div className="text-xs text-zinc-500">{user.email}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="user-reset-password-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">New password (min 8 chars)</div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 pr-10 text-sm focus:border-zinc-900 focus:outline-none font-mono"
                data-testid="user-reset-password-new"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
                data-testid="user-reset-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">Confirm password</div>
            <input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none font-mono"
              data-testid="user-reset-password-confirm"
            />
          </div>
          <div className="text-xs text-zinc-500 border border-zinc-200 bg-zinc-50 p-2">
            The user can sign in immediately with this new password. Share it securely.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
            data-testid="user-reset-password-submit"
          >
            {saving ? "Updating…" : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- Per-user "Assign exams" modal -----
function AssignExamsModal({ user, onClose, onSaved }) {
  const [exams, setExams] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [ex, al] = await Promise.all([
          api.get("/exams"),
          api.get(`/users/${user.user_id}/exam-allocations`),
        ]);
        setExams(ex.data || []);
        const ids = new Set((al.data.exams || []).map((e) => e.exam_id));
        setAssigned(ids);
        setInitial(new Set(ids));
      } finally { setLoading(false); }
    })();
  }, [user.user_id]);

  const toggle = (eid) => {
    const next = new Set(assigned);
    if (next.has(eid)) next.delete(eid); else next.add(eid);
    setAssigned(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.user_id}/exam-allocations`, { exam_ids: [...assigned] });
      toast.success(`Exam access updated for ${user.name}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const filtered = exams.filter((e) => !q || (e.title || "").toLowerCase().includes(q.toLowerCase()));
  const added = [...assigned].filter((x) => !initial.has(x)).length;
  const removed = [...initial].filter((x) => !assigned.has(x)).length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="assign-exams-modal">
      <div className="bg-white max-w-2xl w-full max-h-[85vh] flex flex-col border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Assign exams</div>
          <div className="font-display text-lg font-bold">{user.name}</div>
          <div className="text-xs text-zinc-500">Tick the exams this student can access. Unticked exams remain visible to other students unless you lock them down.</div>
        </div>
        <div className="p-4 border-b border-zinc-200">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search exams…"
              className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="assign-exams-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No exams in your institution.</div>
          ) : (
            filtered.map((e) => (
              <label key={e.exam_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`assign-exam-row-${e.exam_id}`}>
                <input
                  type="checkbox"
                  checked={assigned.has(e.exam_id)}
                  onChange={() => toggle(e.exam_id)}
                  data-testid={`assign-exam-check-${e.exam_id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.title}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{e.status} · {e.duration_minutes}min</div>
                </div>
              </label>
            ))
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between">
          <div className="text-xs text-zinc-500" data-testid="assign-exams-summary">
            {assigned.size} selected · <span className="text-green-700">+{added}</span> / <span className="text-red-700">-{removed}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50" data-testid="assign-exams-save">
              {saving ? "Saving…" : "Save allocations"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Bulk assign exams to multiple students -----
function BulkAssignExamsModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [selectedExams, setSelectedExams] = useState(new Set());
  const [mode, setMode] = useState("add");
  const [uQ, setUQ] = useState("");
  const [eQ, setEQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [uu, ee] = await Promise.all([
        api.get("/users?role=student"),
        api.get("/exams"),
      ]);
      setUsers(uu.data || []);
      setExams(ee.data || []);
    })();
  }, []);

  const filteredUsers = users.filter((u) => !uQ || (u.name || "").toLowerCase().includes(uQ.toLowerCase()) || (u.email || "").toLowerCase().includes(uQ.toLowerCase()));
  const filteredExams = exams.filter((e) => !eQ || (e.title || "").toLowerCase().includes(eQ.toLowerCase()));

  const toggle = (set, setter, id) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); setter(n); };

  const submit = async () => {
    if (selectedStudents.size === 0 || selectedExams.size === 0) { toast.error("Pick at least one student and one exam"); return; }
    setSaving(true);
    try {
      const r = await api.post("/exams/bulk-allocate", {
        student_ids: [...selectedStudents],
        exam_ids: [...selectedExams],
        mode,
      });
      toast.success(`${r.data.affected_exams} exam${r.data.affected_exams === 1 ? "" : "s"} × ${r.data.students} student${r.data.students === 1 ? "" : "s"} — ${mode}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="bulk-assign-modal">
      <div className="bg-white max-w-4xl w-full max-h-[85vh] flex flex-col border border-zinc-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Bulk assign</div>
          <div className="font-display text-lg font-bold">Assign exams to multiple students</div>
        </div>
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Mode</div>
          {["add", "remove", "set"].map((m) => (
            <label key={m} className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} data-testid={`bulk-mode-${m}`} />
              <span className={mode === m ? "font-semibold" : ""}>{m === "add" ? "Add" : m === "remove" ? "Remove" : "Replace"}</span>
            </label>
          ))}
          <div className="text-[11px] text-zinc-500 ml-4">
            {mode === "add" && "Grant access without removing existing allocations."}
            {mode === "remove" && "Revoke access for the selected students."}
            {mode === "set" && "Replace the entire allocation list per exam with the selected students."}
          </div>
        </div>
        <div className="flex-1 overflow-hidden grid grid-cols-2">
          {/* Students */}
          <div className="flex flex-col border-r border-zinc-200">
            <div className="p-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Students ({selectedStudents.size})</div>
              <input value={uQ} onChange={(e) => setUQ(e.target.value)} placeholder="Search…" className="w-full border border-zinc-300 px-3 py-1.5 text-sm focus:border-zinc-900 focus:outline-none" data-testid="bulk-students-search" />
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="bulk-students-list">
              {filteredUsers.map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selectedStudents.has(u.user_id)} onChange={() => toggle(selectedStudents, setSelectedStudents, u.user_id)} data-testid={`bulk-student-${u.user_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {/* Exams */}
          <div className="flex flex-col">
            <div className="p-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Exams ({selectedExams.size})</div>
              <input value={eQ} onChange={(e) => setEQ(e.target.value)} placeholder="Search…" className="w-full border border-zinc-300 px-3 py-1.5 text-sm focus:border-zinc-900 focus:outline-none" data-testid="bulk-exams-search" />
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="bulk-exams-list">
              {filteredExams.map((e) => (
                <label key={e.exam_id} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selectedExams.has(e.exam_id)} onChange={() => toggle(selectedExams, setSelectedExams, e.exam_id)} data-testid={`bulk-exam-${e.exam_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">{e.status}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900">Cancel</button>
          <button onClick={submit} disabled={saving || selectedStudents.size === 0 || selectedExams.size === 0} className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50" data-testid="bulk-assign-submit">
            {saving ? "Applying…" : `Apply "${mode}"`}
          </button>
        </div>
      </div>
    </div>
  );
}


function PendingRoleRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/role-requests?status=pending");
      setRows(r.data || []);
    } catch {
      // Non-admin — skip silently
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (rid, action) => {
    setBusyId(rid);
    try {
      await api.post(`/role-requests/${rid}/${action}`);
      toast.success(action === "approve" ? "Role approved" : "Request rejected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  if (loading || rows.length === 0) return null;

  return (
    <div className="mb-6 border border-amber-300 bg-amber-50/60" data-testid="role-requests-section">
      <div className="px-5 py-3 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} weight="fill" className="text-amber-700" />
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-800 font-semibold">
            {rows.length} role request{rows.length === 1 ? "" : "s"} pending approval
          </div>
        </div>
      </div>
      <div className="divide-y divide-amber-200">
        {rows.map((r) => (
          <div key={r.request_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`role-req-${r.request_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-900">
                {r.user?.name || "Unknown"} <span className="text-zinc-400 font-mono text-[11px]">· {r.user?.mobile ? `+${r.user.mobile}` : (r.user?.email || "no contact")}</span>
              </div>
              <div className="text-xs text-zinc-600">
                Wants <span className="font-semibold text-zinc-900 capitalize">{r.requested_role.replace(/_/g, " ")}</span>
                <span className="text-zinc-400"> · currently </span>
                <span className="capitalize">{r.current_role.replace(/_/g, " ")}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => act(r.request_id, "approve")}
                disabled={busyId === r.request_id}
                className="border border-emerald-600 text-emerald-700 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-50"
                data-testid={`role-req-approve-${r.request_id}`}
              >
                Approve
              </button>
              <button
                onClick={() => act(r.request_id, "reject")}
                disabled={busyId === r.request_id}
                className="border border-red-500 text-red-700 px-3 py-1.5 text-xs font-semibold hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                data-testid={`role-req-reject-${r.request_id}`}
              >
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
